import React from 'react';
import type { DraggableProvided } from './types';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useDndA11yContext } from './DndA11yContext';

export interface DraggableProps {
  draggableId: string;
  index: number;
  isDragDisabled?: boolean;
  disableInteractiveElementBlocking?: boolean;
  shouldRespectForcePress?: boolean;
  children: (provided: DraggableProvided) => any;
}

export function Draggable(props: DraggableProps) {
  const a11y = useDndA11yContext();
  const elementRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!elementRef.current) {
      return;
    }

    return draggable({
      element: elementRef.current,
      canDrag: () => !props.isDragDisabled,
      getInitialData: () => {
        const sourceDroppableId =
          elementRef.current?.closest?.('[data-eb-droppable-id]')?.getAttribute('data-eb-droppable-id') ?? undefined;
        return {
          draggableId: props.draggableId,
          sourceDroppableId,
        };
      },
    });
  }, [props.draggableId, props.isDragDisabled]);

  const provided: DraggableProvided = React.useMemo(
    () => ({
      draggableProps: {
        'data-eb-draggable-id': props.draggableId,
      },
      dragHandleProps: {
        'data-eb-draggable-handle-id': props.draggableId,
        tabIndex: 0,
        role: 'button',
        style: props.isDragDisabled ? undefined : { cursor: 'grab' },
        onFocus: () => a11y?.onHandleFocus(props.draggableId),
        onBlur: () => a11y?.onHandleBlur(props.draggableId),
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
            return;
          }
          e.preventDefault();

          if (props.isDragDisabled) {
            a11y?.flashKeyboardError(props.draggableId);
            return;
          }

          const sourceDroppableId =
            elementRef.current?.closest?.('[data-eb-droppable-id]')?.getAttribute('data-eb-droppable-id') ?? undefined;

          a11y?.onHandleKeyDown({
            draggableId: props.draggableId,
            direction: e.key === 'ArrowLeft' ? -1 : 1,
            sourceDroppableId,
          });
        },
      },
      innerRef: (element) => {
        elementRef.current = element;
      },
      containerClassName: a11y?.getDraggableClassName(props.draggableId),
    }),
    [a11y, props.draggableId, props.isDragDisabled]
  );

  return props.children(provided) as any;
}
